import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaCheckCircle,
  FaPlus,
  FaMinus,
  FaCocktail,
  FaUtensils,
  FaChevronDown,
} from "react-icons/fa";
import PaymentCheckout from "../components/booking/PaymentCheckout";
import bookingService from "../services/bookingService";
import emailService from "../services/emailService";

const COUNTRY_CODES = [
  { code: "AL", dial: "+355", flag: "🇦🇱", name: "Albania" },
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italy" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Germany" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "UK" },
  { code: "US", dial: "+1", flag: "🇺🇸", name: "USA" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "Spain" },
  { code: "GR", dial: "+30", flag: "🇬🇷", name: "Greece" },
  { code: "AT", dial: "+43", flag: "🇦🇹", name: "Austria" },
  { code: "CH", dial: "+41", flag: "🇨🇭", name: "Switzerland" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Netherlands" },
  { code: "BE", dial: "+32", flag: "🇧🇪", name: "Belgium" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Poland" },
  { code: "CZ", dial: "+420", flag: "🇨🇿", name: "Czech Republic" },
  { code: "HU", dial: "+36", flag: "🇭🇺", name: "Hungary" },
  { code: "RS", dial: "+381", flag: "🇷🇸", name: "Serbia" },
  { code: "XK", dial: "+383", flag: "🇽🇰", name: "Kosovo" },
  { code: "MK", dial: "+389", flag: "🇲🇰", name: "North Macedonia" },
  { code: "ME", dial: "+382", flag: "🇲🇪", name: "Montenegro" },
  { code: "HR", dial: "+385", flag: "🇭🇷", name: "Croatia" },
  { code: "BA", dial: "+387", flag: "🇧🇦", name: "Bosnia" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Turkey" },
  { code: "RO", dial: "+40", flag: "🇷🇴", name: "Romania" },
  { code: "BG", dial: "+359", flag: "🇧🇬", name: "Bulgaria" },
  { code: "CA", dial: "+1", flag: "🇨🇦", name: "Canada" },
  { code: "AU", dial: "+61", flag: "🇦🇺", name: "Australia" },
];

const AddExtras = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Verify, 2: Select Extras, 3: Payment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bookingRef, setBookingRef] = useState("");
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("+355");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [dialOpen, setDialOpen] = useState(false);
  const dialRef = useRef(null);
  // Compose full phone for service calls.
  // Handles all input styles: "69 123 4567", "069123456", "+35569123456", "35569123456"
  const phone = (() => {
    if (!phoneLocal.trim()) return "";
    const dialDigits = dialCode.replace(/\D/g, ""); // e.g. "355" from "+355"
    let local = phoneLocal.trim().replace(/\D/g, ""); // digits only
    local = local.replace(/^0+/, ""); // strip leading zeros
    if (local.startsWith(dialDigits)) local = local.slice(dialDigits.length); // strip accidental prefix
    local = local.replace(/^0+/, ""); // strip zeros again after stripping prefix
    return local ? `${dialCode}${local}` : "";
  })();
  const [existingBooking, setExistingBooking] = useState(null);

  // Close dial-code dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dialRef.current && !dialRef.current.contains(e.target))
        setDialOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const [collapsedSections, setCollapsedSections] = useState({
    drinks: true,
    food: true,
  });

  const [selectedExtras, setSelectedExtras] = useState({
    drinks: {},
    food: {},
  });

  // Available extras options
  const extrasOptions = {
    drinks: [
      { id: "water", name: "Bottled Water", price: 2 },
      { id: "softdrink", name: "Soft Drink", price: 3 },
      { id: "beer", name: "Local Beer", price: 5 },
      { id: "champagne", name: "Champagne", price: 30 },
    ],
    food: [
      { id: "seafood-salad", name: "Cold seafood salad", price: 20 },
      { id: "mix-crudo", name: "Mix crudo", price: 45 },
      { id: "linguine-seafood", name: "Linguine seafood", price: 15 },
      { id: "risotto-seafood", name: "Risotto seafood", price: 15 },
      { id: "seafood-fried", name: "Seafood mix fried", price: 30 },
      { id: "seafood-grilled", name: "Seafood mix grilled", price: 30 },
      // { id: "chocolate-souffle", name: "Chocolaté soufflé", price: 5 },
    ],
  };

  // Calculate extras total
  const calculateExtrasTotal = () => {
    let total = 0;

    // Drinks
    Object.entries(selectedExtras.drinks).forEach(([drinkId, quantity]) => {
      const drink = extrasOptions.drinks.find((d) => d.id === drinkId);
      if (drink) total += drink.price * quantity;
    });

    // Food
    Object.entries(selectedExtras.food).forEach(([foodId, quantity]) => {
      const food = extrasOptions.food.find((f) => f.id === foodId);
      if (food) total += food.price * quantity;
    });

    return total;
  };

  // Verify booking
  const handleVerifyBooking = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() && !phone.trim()) {
      setError("Please enter your email address or phone number.");
      return;
    }
    setLoading(true);

    try {
      const booking = await bookingService.getBookingByReferenceAndContact(
        bookingRef,
        email,
        phone,
      );

      if (!booking) {
        setError(
          "Booking not found. Please check your reference, email and phone number.",
        );
        setLoading(false);
        return;
      }

      setExistingBooking(booking);
      setStep(2);
    } catch (err) {
      console.error("Error verifying booking:", err);
      setError("Failed to verify booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle extras change
  const handleExtrasChange = (type, itemId, change) => {
    setSelectedExtras((prev) => {
      const currentQuantity = prev[type][itemId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change);

      const newExtras = { ...prev[type] };

      if (newQuantity === 0) {
        delete newExtras[itemId];
      } else {
        newExtras[itemId] = newQuantity;
      }

      return {
        ...prev,
        [type]: newExtras,
      };
    });
  };

  // Toggle section collapse
  const toggleSection = (section) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Proceed to payment
  const handleProceedToPayment = () => {
    const total = calculateExtrasTotal();
    if (total === 0) {
      setError("Please select at least one extra item.");
      return;
    }
    setError("");
    setStep(3);
  };

  // Payment success handler
  const handlePaymentSuccess = async (paymentData) => {
    try {
      const extrasTotal = calculateExtrasTotal();

      // Update booking with new extras
      await bookingService.addExtrasToBooking(
        existingBooking.id,
        selectedExtras,
        extrasTotal,
        existingBooking._isExternal || false,
      );

      // Send extras confirmation email
      if (existingBooking.bookingReference) {
        const emailData = {
          bookingReference: existingBooking.bookingReference,
          customerInfo: existingBooking.customerInfo || {
            email,
            name: "Guest",
          },
          serviceType: existingBooking.serviceType,
          date: existingBooking.date,
        };

        // Send email asynchronously
        emailService
          .sendExtrasAddedConfirmation(emailData, selectedExtras, extrasTotal)
          .catch((err) => {
            console.error("Failed to send extras confirmation email:", err);
          });
      }

      // Navigate to success page
      navigate(
        `/booking-success/${existingBooking.id}?extrasAdded=true&ref=${existingBooking.bookingReference}`,
      );
    } catch (err) {
      console.error("Error updating booking:", err);
      setError(
        "Payment successful but failed to update booking. Please contact support.",
      );
    }
  };

  // Payment error handler
  const handlePaymentError = (error) => {
    setError("Payment failed. Please try again.");
    console.error("Payment error:", error);
  };

  // Render extras item
  const renderExtrasItem = (item, type) => {
    const quantity = selectedExtras[type][item.id] || 0;

    return (
      <div
        key={item.id}
        className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-2.5 sm:p-3 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
            {item.name}
          </p>
          <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
            €{item.price.toFixed(2)}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => handleExtrasChange(type, item.id, -1)}
            disabled={quantity === 0}
            className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors"
          >
            <FaMinus className="text-xs sm:text-sm text-gray-700 dark:text-gray-300" />
          </button>
          <span className="min-w-[1.5rem] sm:min-w-[2rem] text-center font-semibold text-xs sm:text-sm text-gray-900 dark:text-white">
            {quantity}
          </span>
          <button
            onClick={() => handleExtrasChange(type, item.id, 1)}
            className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors"
          >
            <FaPlus className="text-xs sm:text-sm text-white" />
          </button>
        </div>
      </div>
    );
  };

  // Step 1: Verify Booking
  if (step === 1) {
    return (
      <div className="max-w-2xl mx-auto py-8 sm:py-12 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="px-6 sm:px-8 py-6 sm:py-8 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <span className="text-2xl sm:text-3xl">🎯</span>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  Add Extras to Your Booking
                </h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                  Enhance your experience with drinks &amp; food
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 sm:p-8">
            <div className="mb-6">
              <div className="flex items-start gap-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <FaCheckCircle className="text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm sm:text-base text-gray-900 dark:text-white">
                  <p className="font-semibold mb-1">Already have a booking?</p>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                    Enter your booking reference and email to add extras to your
                    existing reservation.
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">
                  {error}
                </p>
              </div>
            )}

            <form onSubmit={handleVerifyBooking} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Booking Reference
                </label>
                <input
                  type="text"
                  value={bookingRef}
                  onChange={(e) => setBookingRef(e.target.value.toUpperCase())}
                  placeholder="e.g., BK123456"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:border-transparent text-sm sm:text-base"
                  required
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  You received this in your booking confirmation email
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Address
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    (optional)
                  </span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent text-sm sm:text-base"
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Use the same email you used when booking
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="text-xs text-gray-400 font-medium">OR</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Phone Number
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    (optional)
                  </span>
                </label>
                <div className="flex gap-2">
                  {/* Country code picker */}
                  <div ref={dialRef} className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setDialOpen((o) => !o)}
                      className="flex items-center gap-1.5 h-full px-3 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                    >
                      <span className="text-base leading-none">
                        {COUNTRY_CODES.find((c) => c.dial === dialCode)?.flag ||
                          "🌐"}
                      </span>
                      <span className="font-mono">{dialCode}</span>
                      <FaChevronDown className="text-gray-400 text-xs" />
                    </button>
                    {dialOpen && (
                      <ul className="absolute z-50 mt-1 left-0 w-56 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl text-sm">
                        {COUNTRY_CODES.map((c) => (
                          <li key={c.code}>
                            <button
                              type="button"
                              onClick={() => {
                                setDialCode(c.dial);
                                setDialOpen(false);
                              }}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-sky-50 dark:hover:bg-gray-700 text-left ${
                                c.dial === dialCode
                                  ? "bg-sky-50 dark:bg-gray-700 font-semibold"
                                  : ""
                              }`}
                            >
                              <span className="text-base">{c.flag}</span>
                              <span className="flex-1 text-gray-700 dark:text-gray-200">
                                {c.name}
                              </span>
                              <span className="font-mono text-gray-400 text-xs">
                                {c.dial}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/* Local number */}
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={phoneLocal}
                    onChange={(e) =>
                      setPhoneLocal(e.target.value.replace(/[^\d\s-]/g, ""))
                    }
                    placeholder="69 123 4567"
                    className="flex-1 min-w-0 px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 focus:border-transparent text-sm sm:text-base"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  Use the same phone number you used when booking
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 disabled:bg-gray-400 text-white font-semibold py-3.5 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <FaSearch />
                    Find My Booking
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Select Extras
  if (step === 2) {
    const extrasTotal = calculateExtrasTotal();

    return (
      <div className="max-w-4xl mx-auto py-8 sm:py-12 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="px-6 sm:px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Select Your Extras
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Add drinks, food &amp; more to enhance your{" "}
              {existingBooking.serviceType} experience
            </p>
          </div>

          {/* Booking Info */}
          <div className="px-6 sm:px-8 py-5 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Booking Ref:
                </span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  {existingBooking.bookingReference}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Date:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  {new Date(existingBooking.date).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  Guests:
                </span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  {existingBooking.numberOfGuests}
                </span>
              </div>
            </div>
          </div>

          {/* Extras Selection */}
          <div className="p-6 sm:p-8 space-y-4 sm:space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">
                  {error}
                </p>
              </div>
            )}

            {
              /* Drinks */
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection("drinks")}
                  className="w-full flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 px-3 sm:px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <FaCocktail className="text-cyan-600 dark:text-cyan-400 text-base sm:text-lg" />
                    <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                      Drinks
                    </span>
                  </div>
                  <FaChevronDown
                    className="text-gray-500 text-xs sm:text-sm transition-transform duration-300"
                    style={{
                      transform: collapsedSections.drinks
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                    }}
                  />
                </button>
                <div
                  style={{
                    display: "grid",
                    gridTemplateRows: collapsedSections.drinks ? "0fr" : "1fr",
                    transition: "grid-template-rows 0.32s ease",
                  }}
                >
                  <div style={{ overflow: "hidden" }}>
                    <div className="p-3 sm:p-4 space-y-2 sm:space-y-2.5 bg-white dark:bg-gray-800">
                      {extrasOptions.drinks.map((drink) =>
                        renderExtrasItem(drink, "drinks"),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            }
            {/* Food */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection("food")}
                className="w-full flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 px-3 sm:px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <FaUtensils className="text-amber-500 dark:text-amber-400 text-base sm:text-lg" />
                  <div>
                    <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                      Food
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      🛥️ Another boat brings food to you at sea
                    </p>
                  </div>
                </div>
                <FaChevronDown
                  className="text-gray-500 text-xs sm:text-sm transition-transform duration-300"
                  style={{
                    transform: collapsedSections.food
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                  }}
                />
              </button>
              <div
                style={{
                  display: "grid",
                  gridTemplateRows: collapsedSections.food ? "0fr" : "1fr",
                  transition: "grid-template-rows 0.32s ease",
                }}
              >
                <div style={{ overflow: "hidden" }}>
                  <div className="p-3 sm:p-4 space-y-2 sm:space-y-2.5 bg-white dark:bg-gray-800">
                    {extrasOptions.food.map((item) =>
                      renderExtrasItem(item, "food"),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with Total and Button */}
          <div className="px-6 sm:px-8 py-5 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Extras Total
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  €{extrasTotal.toFixed(2)}
                </p>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 sm:flex-none px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold rounded-lg transition-colors text-sm sm:text-base"
                >
                  Back
                </button>
                <button
                  onClick={handleProceedToPayment}
                  className="flex-1 sm:flex-none px-6 py-3 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200 text-sm sm:text-base"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Payment
  if (step === 3) {
    // Convert selectedExtras to the format expected by PaymentCheckout
    const extrasForPayment = {
      drinks: Object.entries(selectedExtras.drinks).map(([id, quantity]) => {
        const drink = extrasOptions.drinks.find((d) => d.id === id);
        return { ...drink, quantity };
      }),
      food: Object.entries(selectedExtras.food).map(([id, quantity]) => {
        const item = extrasOptions.food.find((f) => f.id === id);
        return { ...item, quantity };
      }),
    };

    const paymentData = {
      serviceId: existingBooking.serviceId,
      serviceType: existingBooking.serviceType,
      date: existingBooking.date,
      timeSlot: existingBooking.timeSlot,
      numberOfGuests: existingBooking.numberOfGuests,
      totalPrice: calculateExtrasTotal(),
      extras: extrasForPayment,
      customerInfo: {
        fullName: existingBooking.customerInfo?.name || "Guest",
        email: existingBooking.customerInfo?.email || email,
        phone: existingBooking.customerInfo?.phone || "",
      },
    };

    return (
      <div className="max-w-4xl mx-auto py-8 sm:py-12 px-4">
        <div className="mb-6">
          <button
            onClick={() => setStep(2)}
            className="text-sm sm:text-base text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium flex items-center gap-2"
          >
            ← Back to Extras Selection
          </button>
        </div>
        <PaymentCheckout
          bookingData={paymentData}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          createBooking={async () => ({
            bookingId: existingBooking.id,
            bookingReference: existingBooking.bookingReference,
          })}
        />
      </div>
    );
  }

  return null;
};

export default AddExtras;
